import React, { useState, useEffect } from 'react';
import { X, ArrowRight, AlertCircle, Check } from 'lucide-react';

const BulkTrimesterTransferModal = ({ apiCall, selectedYear, currentTrimester, onClose, onSuccess, showError, showSuccess }) => {
  const [fromTrimester, setFromTrimester] = useState('1');
  const [toTrimester, setToTrimester] = useState('2');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Load preview when trimesters selected
  useEffect(() => {
    if (fromTrimester && toTrimester) {
      loadPreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTrimester, toTrimester]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/bus/transfer-preview?academicYear=${selectedYear}&fromTrimester=${fromTrimester}&toTrimester=${toTrimester}`);
      setPreview(data);
    } catch (error) {
      showError('Error loading preview: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!window.confirm(`Are you sure you want to transfer ${preview.enrollmentsCount} students from T${fromTrimester} to T${toTrimester}?

This will:
- Create ${preview.enrollmentsCount} new enrollments in T${toTrimester}
- Update FamilyYearlyBalance for ${preview.familiesCount} families
- Keep original T${fromTrimester} enrollments (marked as transferred)

This action cannot be undone!`)) {
      return;
    }

    try {
      setProcessing(true);
      const result = await apiCall('/bus/bulk-transfer', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: selectedYear,
          fromTrimester: parseInt(fromTrimester),
          toTrimester: parseInt(toTrimester)
        })
      });

      showSuccess(`Successfully transferred ${result.transferred} students to T${toTrimester}`);
      onSuccess();
      onClose();
    } catch (error) {
      showError('Transfer failed: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Bulk Trimester Transfer</h2>
            <p className="text-sm text-gray-600 mt-1">Transfer all active enrollments to next trimester</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Trimester Selection */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-4">Select Trimesters</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">From Trimester</label>
                <select
                  value={fromTrimester}
                  onChange={(e) => setFromTrimester(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Trimester 1</option>
                  <option value="2">Trimester 2</option>
                </select>
              </div>

              <div className="mt-6">
                <ArrowRight className="text-blue-600" size={32} />
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">To Trimester</label>
                <select
                  value={toTrimester}
                  onChange={(e) => setToTrimester(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="2">Trimester 2</option>
                  <option value="3">Trimester 3</option>
                </select>
              </div>
            </div>
          </div>

          {/* Preview */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading preview...</span>
            </div>
          ) : preview ? (
            <>
              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Students</div>
                  <div className="text-2xl font-bold text-blue-600">{preview.enrollmentsCount}</div>
                </div>
                <div className="bg-white border-2 border-green-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Families</div>
                  <div className="text-2xl font-bold text-green-600">{preview.familiesCount}</div>
                </div>
                <div className="bg-white border-2 border-purple-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Total Revenue</div>
                  <div className="text-2xl font-bold text-purple-600">${preview.totalRevenue?.toFixed(2)}</div>
                </div>
                <div className="bg-white border-2 border-orange-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Outstanding</div>
                  <div className="text-2xl font-bold text-orange-600">${preview.totalOutstanding?.toFixed(2)}</div>
                </div>
              </div>

              {/* Lines Breakdown */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Lines Breakdown</h4>
                <div className="space-y-2">
                  {preview.lineBreakdown?.map(line => (
                    <div key={line.LineID} className="flex justify-between items-center">
                      <span className="text-gray-700">Line {line.LineNumber} - {line.LineName}</span>
                      <span className="font-semibold text-gray-900">{line.StudentCount} students</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What Will Happen */}
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-2">What will happen:</h4>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li>✓ Create {preview.enrollmentsCount} new enrollments in Trimester {toTrimester}</li>
                      <li>✓ Copy all student details, line, bus, and pricing information</li>
                      <li>✓ Reset payment status to "Unpaid" (balance = full price)</li>
                      <li>✓ Update FamilyYearlyBalance for {preview.familiesCount} families</li>
                      <li>✓ Original T{fromTrimester} enrollments remain unchanged</li>
                      <li>⚠ This action cannot be undone</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {preview.alreadyEnrolled > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">Warning</h4>
                      <p className="text-sm text-red-800">
                        {preview.alreadyEnrolled} students are already enrolled in T{toTrimester}. 
                        They will be skipped to avoid duplicates.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {preview.eligibleCount === 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-1">No students to transfer</h4>
                      <p className="text-sm text-yellow-800">
                        All students in T{fromTrimester} are either already enrolled in T{toTrimester} or have been cancelled.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-3 justify-end bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            disabled={processing || !preview || preview.eligibleCount === 0}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Processing...
              </>
            ) : (
              <>
                <Check size={20} />
                Transfer {preview?.eligibleCount || 0} Students
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkTrimesterTransferModal;
